<script>
import { pinStatus, pinLogin, logout, getUser } from '@/auth'
import { lockState, unlock, closeSwitch, dismissSaver } from '@/lock'

// Фотографии техники компании (public/lock/1.jpg … N.jpg) меняются сами, пока экран открыт
const IMAGE_COUNT = 14
const SLIDE_MS = 8 * 1000
// В окне ввода ПИН-кода без нажатий экран возвращается к заставке
const PIN_IDLE_MS = 30 * 1000

const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', null, '0', 'back']
// Что закрывает заставку (кроме нажатия клавиши)
const SAVER_EVENTS = ['mousemove', 'mousedown', 'touchstart', 'wheel']

function shuffle(list) {
  const a = list.slice()
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    const t = a[i]
    a[i] = a[j]
    a[j] = t
  }
  return a
}

export default {
  name: 'LockScreen',
  data() {
    return {
      view: lockState.switching ? 'pin' : 'clock',
      pin: '',
      busy: false,
      error: '',
      shake: false,
      now: Date.now(),
      // пользователь, выбранный в меню профиля при смене пользователя; на экране блокировки не выбирается: его определяет ПИН-код
      selected: lockState.pick,
      banUntil: 0,

      order: shuffle(Array.from({ length: IMAGE_COUNT }, (_, i) => i + 1)),
      position: 0,
      slide: null,
      slideKey: 0,
      timers: { clock: null, slide: null, pin: null },
      // до этого момента заставка не реагирует на движение
      sleepUntil: 0
    }
  },
  computed: {
    keys() {
      return KEYS
    },
    switching() {
      return lockState.switching
    },
    // Заставка по простою: закрывается движением мыши или нажатием, ПИН-код не нужен
    saver() {
      return lockState.saver && !lockState.locked && !lockState.switching
    },
    user() {
      return getUser() || {}
    },
    userName() {
      return this.user.name || this.user.username || ''
    },
    slides() {
      return this.slide ? [this.slide] : []
    },
    timeText() {
      return new Date(this.now).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
    },
    dateText() {
      const text = new Date(this.now).toLocaleDateString('ru-RU', { weekday: 'long', day: 'numeric', month: 'long' })
      return text.charAt(0).toUpperCase() + text.slice(1)
    },
    banLeft() {
      return Math.max(0, Math.ceil((this.banUntil - this.now) / 1000))
    },
    banned() {
      return this.banLeft > 0
    },
    banText() {
      const m = Math.floor(this.banLeft / 60)
      const s = this.banLeft % 60
      return m + ':' + (s < 10 ? '0' : '') + s
    },
    title() {
      return this.selected ? this.selected.name : 'Введите ПИН-код'
    },
    subText() {
      return this.selected ? 'Введите ПИН-код этого пользователя' : 'Пользователь определяется по ПИН-коду'
    }
  },
  created() {
    // у заставки нет ввода кода, состояние бана не нужно
    if (!lockState.saver) this.loadStatus()
    this.showNext()
  },
  mounted() {
    this.timers.clock = setInterval(() => { this.now = Date.now() }, 1000)
    this.timers.slide = setInterval(this.showNext, SLIDE_MS)
    window.addEventListener('keydown', this.onKey, true)
    document.addEventListener('focusin', this.keepFocus)
    // Страница под экраном не должна получать ввод: фокус уходит на экран блокировки
    this.grabFocus()
    if (this.view === 'pin') this.armPinIdle()
    if (this.saver) {
      // сразу после включения заставки случайное движение не закрывает её
      this.sleepUntil = Date.now() + 800
      SAVER_EVENTS.forEach((name) => window.addEventListener(name, this.onSaverEvent, { passive: true, capture: true }))
    }
  },
  beforeDestroy() {
    clearInterval(this.timers.clock)
    clearInterval(this.timers.slide)
    clearTimeout(this.timers.pin)
    window.removeEventListener('keydown', this.onKey, true)
    document.removeEventListener('focusin', this.keepFocus)
    SAVER_EVENTS.forEach((name) => window.removeEventListener(name, this.onSaverEvent, { passive: true, capture: true }))
  },
  methods: {
    grabFocus() {
      if (document.activeElement && document.activeElement !== document.body && document.activeElement.blur) document.activeElement.blur()
      if (this.$el && this.$el.focus) this.$el.focus()
    },
    keepFocus(e) {
      if (this.$el && !this.$el.contains(e.target)) this.grabFocus()
    },

    // Сколько секунд устройство ещё заблокировано (сервер — источник правды)
    loadStatus() {
      pinStatus()
        .then((data) => this.setBan(data.banned_for))
        .catch(() => {
          // бан всё равно проверит сервер при вводе
        })
    },
    setBan(seconds) {
      this.now = Date.now()
      this.banUntil = seconds > 0 ? this.now + seconds * 1000 : 0
      if (seconds > 0) {
        this.pin = ''
        this.error = ''
      }
    },

    // Следующая фотография: сначала загружается, потом плавно сменяет текущую
    showNext() {
      const src = '/lock/' + this.order[this.position % this.order.length] + '.jpg'
      this.position++
      const img = new Image()
      const apply = () => {
        this.slide = { key: ++this.slideKey, src }
      }
      img.onload = apply
      img.onerror = () => {
        // фотография не загрузилась — остаётся прежняя, следующая попытка по таймеру
      }
      img.src = src
    },

    // Заставка: движение мыши, касание, прокрутка закрывают её
    onSaverEvent() {
      if (this.saver && Date.now() >= this.sleepUntil) dismissSaver()
    },
    wake() {
      if (this.saver) {
        dismissSaver()
        return
      }
      if (this.view !== 'clock') return
      this.view = 'pin'
      this.pin = ''
      this.error = ''
      this.armPinIdle()
    },
    toClock() {
      this.view = 'clock'
      this.pin = ''
      this.error = ''
      this.selected = null
      clearTimeout(this.timers.pin)
    },
    armPinIdle() {
      clearTimeout(this.timers.pin)
      // из режима смены пользователя экран сам не уходит: его закрывают кнопкой
      if (this.switching) return
      this.timers.pin = setTimeout(this.toClock, PIN_IDLE_MS)
    },

    press(digit) {
      if (this.busy || this.banned || this.pin.length >= 4) return
      this.pin += digit
      this.error = ''
      this.armPinIdle()
      if (this.pin.length === 4) this.submit()
    },
    erase() {
      if (this.busy) return
      this.pin = this.pin.slice(0, -1)
      this.error = ''
      this.armPinIdle()
    },
    onKey(e) {
      // сочетания браузера (обновить страницу, закрыть вкладку) не трогаем
      if (e.ctrlKey || e.metaKey || e.altKey) return
      const digit = /^[0-9]$/.test(e.key) ? e.key : null
      if (this.view === 'clock') {
        if (['Shift', 'Control', 'Alt', 'Meta', 'CapsLock', 'Tab'].indexOf(e.key) !== -1) return
        e.preventDefault()
        if (this.saver) {
          // нажатие закрывает заставку и дальше не идёт: в скрытые поля страницы ничего не попадает
          if (Date.now() >= this.sleepUntil) dismissSaver()
          return
        }
        this.wake()
        if (digit) this.press(digit)
        return
      }
      if (digit) {
        e.preventDefault()
        this.press(digit)
      } else if (e.key === 'Backspace') {
        e.preventDefault()
        this.erase()
      } else if (e.key === 'Escape') {
        e.preventDefault()
        this.cancel()
      }
    },

    submit() {
      this.busy = true
      pinLogin(this.pin, this.selected ? this.selected.id : null)
        .then((data) => {
          unlock()
          // другой пользователь: страница перезагружается, чтобы не осталось данных и прав прежнего
          if (data.switched) window.location.reload()
        })
        .catch((err) => {
          this.busy = false
          this.pin = ''
          this.error = err.message
          this.shake = true
          setTimeout(() => { this.shake = false }, 500)
          if (err.bannedFor) this.setBan(err.bannedFor)
        })
    },

    // Отмена: из режима «сменить пользователя» — назад к работе; с экрана блокировки — к заставке
    cancel() {
      if (this.busy) return
      if (this.switching) closeSwitch()
      else this.toClock()
    },
    toPassword() {
      logout()
      unlock()
      this.$router.push('/login')
    }
  }
}
</script>

<template>
  <div class="lock" :class="{ 'is-pin': view === 'pin' }" role="dialog" aria-modal="true" aria-label="Экран блокировки" tabindex="-1" @click="wake">
    <transition-group name="lock-fade" tag="div" class="lock__slides">
      <div v-for="s in slides" :key="s.key" class="lock__slide" :style="{ backgroundImage: 'url(' + s.src + ')' }"></div>
    </transition-group>
    <div class="lock__shade"></div>

    <div class="lock__logo"><img src="/strnt_logo.png" alt="СпецТехРесурс НТ" /></div>

    <!-- Заставка: время компьютера -->
    <div v-if="view === 'clock'" class="lock__clock">
      <div class="lock__time">{{ timeText }}</div>
      <div class="lock__date">{{ dateText }}</div>
      <div v-if="!saver" class="lock__who"><i class="fas fa-lock"></i> {{ userName }}</div>
      <div class="lock__hint">{{ saver ? 'Подвигайте мышью, чтобы продолжить' : 'Нажмите в любом месте или любую клавишу' }}</div>
    </div>

    <!-- Ввод ПИН-кода -->
    <div v-else class="lock__panel" @click.stop>
      <div class="lock__minitime">{{ timeText }}</div>

      <span v-if="selected" class="lock__avatar lock__avatar--big">{{ (selected.name || '?').charAt(0).toUpperCase() }}</span>

      <h2 class="lock__title">{{ title }}</h2>
      <p class="lock__sub">{{ subText }}</p>

      <div class="lock__dots" :class="{ 'is-shake': shake }" aria-hidden="true">
        <span v-for="n in 4" :key="n" class="lock__dot" :class="{ 'is-filled': pin.length >= n }"></span>
      </div>

      <p class="lock__msg" :class="{ 'is-error': error || banned }" role="status">
        <template v-if="banned"><i class="fas fa-ban"></i> Устройство заблокировано. Повторите через {{ banText }}</template>
        <template v-else-if="error">{{ error }}</template>
        <template v-else-if="busy">Проверяю…</template>
        <template v-else>&nbsp;</template>
      </p>

      <div class="lock__pad" :class="{ 'is-disabled': banned }">
        <template v-for="(k, i) in keys">
          <span v-if="k === null" :key="i" class="lock__pad-gap"></span>
          <button v-else-if="k === 'back'" :key="i" type="button" class="lock__key lock__key--icon" aria-label="Стереть" :disabled="banned || busy || !pin" @click="erase">
            <i class="fas fa-backspace"></i>
          </button>
          <button v-else :key="i" type="button" class="lock__key" :disabled="banned || busy" @click="press(k)">{{ k }}</button>
        </template>
      </div>

      <div class="lock__foot">
        <button type="button" class="lock__link" @click="cancel">
          <i class="fas" :class="switching ? 'fa-arrow-left' : 'fa-clock'"></i> {{ switching ? 'Вернуться к работе' : 'Заставка' }}
        </button>
        <button type="button" class="lock__link" @click="toPassword">
          <i class="fas fa-key"></i> Войти по логину и паролю
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.lock {
  position: fixed;
  inset: 0;
  z-index: 20000;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: #0b1722;
  color: #fff;
  outline: none;
  user-select: none;
  -webkit-user-select: none;
}

/* Фотографии: плавная смена и медленное приближение */
.lock__slides { position: absolute; inset: 0; transition: filter 0.5s ease; }
.lock.is-pin .lock__slides { filter: blur(14px) brightness(0.85); }
.lock__slide {
  position: absolute;
  inset: -3%;
  background-size: cover;
  background-position: center;
  animation: lock-zoom 18s ease-out forwards;
}
.lock-fade-enter-active, .lock-fade-leave-active { transition: opacity 1.6s ease; }
.lock-fade-enter, .lock-fade-leave-to { opacity: 0; }
@keyframes lock-zoom {
  from { transform: scale(1) translate(0, 0); }
  to { transform: scale(1.1) translate(-1.5%, -1%); }
}

.lock__shade {
  position: absolute;
  inset: 0;
  background:
    linear-gradient(to bottom, rgba(6, 14, 22, 0.55) 0%, rgba(6, 14, 22, 0.1) 35%, rgba(6, 14, 22, 0.15) 60%, rgba(6, 14, 22, 0.7) 100%);
  transition: background 0.5s ease;
}
.lock.is-pin .lock__shade { background: rgba(6, 14, 22, 0.55); }

.lock__logo {
  position: absolute;
  top: var(--space-16, 16px);
  left: var(--space-20, 20px);
  padding: 6px 12px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.92);
  line-height: 0;
}
.lock__logo img { height: 2rem; width: auto; }

/* Заставка */
.lock__clock {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 0 16px;
  text-align: center;
  text-shadow: 0 2px 24px rgba(0, 0, 0, 0.55);
  cursor: pointer;
}
.lock__time { font-size: clamp(4.5rem, 16vw, 11rem); font-weight: 200; letter-spacing: 0.02em; line-height: 1; font-variant-numeric: tabular-nums; }
.lock__date { font-size: clamp(1.1rem, 2.6vw, 1.9rem); font-weight: 400; opacity: 0.95; }
.lock__who { margin-top: 18px; padding: 6px 14px; border-radius: 999px; background: rgba(0, 0, 0, 0.35); font-size: 0.95rem; }
.lock__who i { margin-right: 6px; opacity: 0.85; }
.lock__hint { position: fixed; bottom: 28px; left: 0; right: 0; font-size: 0.95rem; opacity: 0.8; animation: lock-hint 3.2s ease-in-out infinite; }
@keyframes lock-hint { 0%, 100% { opacity: 0.45; } 50% { opacity: 0.9; } }

/* Ввод ПИН-кода */
.lock__panel {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: min(92vw, 380px);
  max-height: 100dvh;
  padding: 22px 20px 16px;
  overflow-y: auto;
  text-shadow: 0 1px 10px rgba(0, 0, 0, 0.4);
}
.lock__minitime { font-size: 1.1rem; font-weight: 300; opacity: 0.85; letter-spacing: 0.04em; }

.lock__avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.8rem;
  height: 2.8rem;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.22);
  font-size: 1.2rem;
  font-weight: 600;
}
.lock__avatar--big { width: 3.6rem; height: 3.6rem; margin-top: 14px; font-size: 1.5rem; }

.lock__title { margin: 10px 0 0; max-width: 100%; text-align: center; font-size: 1.35rem; font-weight: 500; text-align: center; }
.lock__sub { margin: 4px 0 14px; font-size: 0.82rem; opacity: 0.75; text-align: center; }

.lock__dots { display: flex; gap: 16px; }
.lock__dot { width: 14px; height: 14px; border: 2px solid rgba(255, 255, 255, 0.85); border-radius: 50%; transition: background 0.12s ease, transform 0.12s ease; }
.lock__dot.is-filled { background: #fff; transform: scale(1.1); }
.lock__dots.is-shake { animation: lock-shake 0.45s ease; }
@keyframes lock-shake {
  0%, 100% { transform: translateX(0); }
  20% { transform: translateX(-10px); }
  40% { transform: translateX(9px); }
  60% { transform: translateX(-6px); }
  80% { transform: translateX(4px); }
}

.lock__msg { min-height: 2.4em; margin: 12px 0 8px; font-size: 0.88rem; text-align: center; }
.lock__msg.is-error { color: #ffc9c9; }
.lock__msg i { margin-right: 4px; }

.lock__pad { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px 16px; transition: opacity 0.2s ease; }
.lock__pad.is-disabled { opacity: 0.4; }
.lock__pad-gap { width: 4.4rem; height: 4.4rem; }
.lock__key {
  width: 4.4rem;
  height: 4.4rem;
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.14);
  backdrop-filter: blur(6px);
  color: #fff;
  font-family: inherit;
  font-size: 1.7rem;
  font-weight: 300;
  cursor: pointer;
  transition: background 0.12s ease, transform 0.08s ease;
}
.lock__key:hover:not(:disabled) { background: rgba(255, 255, 255, 0.28); }
.lock__key:active:not(:disabled) { transform: scale(0.93); background: rgba(255, 255, 255, 0.4); }
.lock__key:disabled { cursor: default; }
.lock__key--icon { font-size: 1.15rem; background: transparent; border-color: transparent; backdrop-filter: none; }

.lock__foot { display: flex; flex-wrap: wrap; justify-content: center; gap: 4px 18px; margin-top: 16px; }
.lock__link {
  padding: 6px 4px;
  border: 0;
  background: none;
  color: rgba(255, 255, 255, 0.8);
  font-family: inherit;
  font-size: 0.85rem;
  cursor: pointer;
}
.lock__link:hover { color: #fff; text-decoration: underline; }
.lock__link i { margin-right: 4px; }

@media (max-height: 640px) {
  .lock__key, .lock__pad-gap { width: 3.5rem; height: 3.5rem; font-size: 1.4rem; }
  .lock__pad { gap: 8px 14px; }
  .lock__sub { display: none; }
}

@media (prefers-reduced-motion: reduce) {
  .lock__slide { animation: none; }
  .lock__hint { animation: none; }
  .lock-fade-enter-active, .lock-fade-leave-active { transition-duration: 0.01s; }
}
</style>
